import Checkbox from '@/common/components/Checkbox'
import Modal from '@/common/components/Modal'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import PrintContext from '@/common/components/PrintContext'
import { useTransactionIdentifierWithReceipt } from '@/common/hooks/queries/useTransactionIdentifierWithReceipt'
import useUserPermissions from '@/common/hooks/useUserPermissions'
import usePrint from '@/common/hooks/usePrint'
import { BillingTransactionIdentifier } from '@/common/types/billing/transactionIdentifier.type'
import { PrinterIcon } from 'lucide-react'
import { Dispatch, SetStateAction, useState } from 'react'

interface ModalSuccessProps {
  data: BillingTransactionIdentifier
  setData: Dispatch<SetStateAction<boolean | BillingTransactionIdentifier>>
  clearForm: () => void
}

const ModalSuccess = ({ data, setData, clearForm }: ModalSuccessProps) => {
  const { hasUserPermission } = useUserPermissions();

  const hasGetTransactionIdentifierPermission = hasUserPermission('Admin::Billing::TransactionIdentifiers::ShowWithReceipt');

  const [doNotPrintReceipt, setDoNotPrintReceipt] = useState(false)

  const [messages, setMessages] = useState<string[] | boolean>(false)

  const { print, contextProps } = usePrint({
    onAfterPrint: () => {
      setData(false);
      clearForm();
    },
  });

  const {
    mutate: getTransactionIdentifier,
    isPending: isGetTransactionIdentifierPending,
  } = useTransactionIdentifierWithReceipt({
    transactionIdentifierId: data?.refundable?.protocol_number,
    onSuccess: (data) => {
      const receipt = data?.eventable?.receipt?.text;

      if (receipt) {
        print({ content: receipt });
      } else {
        setMessages(['Ocorreu um erro ao buscar as informações do comprovante'])
      }
    },
    onError: (error) => {
      setMessages(error.response?.data.errors.base || ['Ocorreu um erro ao buscar as informações do comprovante'])
    },
  })

  const closeModalAndClearForm = () => {
    if (!hasGetTransactionIdentifierPermission || doNotPrintReceipt) {
      setData(false);
      clearForm();
    } else {
      getTransactionIdentifier();
    }
    setDoNotPrintReceipt(false);
  };

  return (
    <>
      <Modal
        isOpen={!!data}
        dataTestid="refund_confirm_modal"
        onConfirm={closeModalAndClearForm}
        Trigger={<></>}
        showClose={false}
        footerIcon={
          (hasGetTransactionIdentifierPermission && !doNotPrintReceipt)
            ? <PrinterIcon strokeWidth={1.5} size={16} className='-mr-2' />
            : undefined
        }
        footer={
          hasGetTransactionIdentifierPermission ?
            !doNotPrintReceipt
              ? 'Imprimir comprovante'
              : 'Finalizar transação sem comprovante'
            : 'Entendido!'
        }
        confirmButtonIsLoading={isGetTransactionIdentifierPending}
        disabled={isGetTransactionIdentifierPending}
        title="Estorno bem sucedido!"
        body={
          <div className="flex flex-col gap-8 py-2">
            <div className="flex flex-col gap-2">
              <div>
                <p className="text-[14px] font-light">Número do estorno</p>
                <p className="text-[16px] text-2xl text-muted">{data?.id}</p>
              </div>
            </div>

            {hasGetTransactionIdentifierPermission && (
              <div>
                <Checkbox
                  name="do_not_print_receipt"
                  checked={doNotPrintReceipt}
                  onChange={(e) => setDoNotPrintReceipt(e.currentTarget.checked)}
                  label='Não desejo imprimir compovante'
                  labelClassName='italic text-white-800 select-none'
                />
              </div>
            )}
          </div>
        }
      />

      <PrintContext {...contextProps} className="text-black-900 px-1" />

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError
          onClose={() => {
            setData(false);
            clearForm();
          }}
        />
      )}
    </>
  )
}

export default ModalSuccess
